import type { ReactNode } from 'react';
import { Dialog, Heading, Modal, ModalOverlay } from 'react-aria-components';
import { Button } from './Button';

export interface ConfirmDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  title: string;
  /** Say plainly what will happen and what is kept or lost. */
  children: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** Destructive actions get the danger button. */
  tone?: 'default' | 'danger';
  isPending?: boolean;
  /** Keeps the confirm button disabled, for example until a typed confirmation matches. */
  isConfirmDisabled?: boolean;
  onConfirm: () => void;
}

/**
 * Confirmation for actions that are hard to undo. Use sparingly: only where a mistake costs something.
 * Focus is trapped, Escape and the cancel button dismiss, and focus starts on cancel for destructive actions.
 */
export function ConfirmDialog({
  isOpen,
  onOpenChange,
  title,
  children,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'default',
  isPending = false,
  isConfirmDisabled = false,
  onConfirm,
}: ConfirmDialogProps) {
  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable={!isPending}
      className="fixed inset-0 z-50 flex items-end justify-center bg-overlay/60 p-4 sm:items-center"
    >
      <Modal className="w-full max-w-md rounded-lg border border-border bg-raised shadow-lg outline-none">
        <Dialog role="alertdialog" className="flex flex-col gap-4 p-6 outline-none">
          <Heading slot="title" className="heading-display text-xl">
            {title}
          </Heading>
          <div className="text-sm text-fg-muted">{children}</div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="secondary"
              autoFocus={tone === 'danger'}
              isDisabled={isPending}
              onPress={() => onOpenChange(false)}
            >
              {cancelLabel}
            </Button>
            <Button
              variant={tone === 'danger' ? 'danger' : 'primary'}
              isDisabled={isPending || isConfirmDisabled}
              onPress={onConfirm}
            >
              {confirmLabel}
            </Button>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
